import { X, Users, Target, TrendingUp, Calendar, Lightbulb, GraduationCap, BarChart3, CheckCircle2, Phone, Download, AlertCircle } from 'lucide-react';
import { generatePDF } from '../services/pdfGenerator';

interface BZTelecomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BZTelecomModal({ isOpen, onClose }: BZTelecomModalProps) {
  if (!isOpen) return null;

  const handleDownloadPDF = async () => {
    await generatePDF(
      'bz-telecom-content',
      `BZ_Telecom_Partenariat_Strategique_${new Date().toISOString().split('T')[0]}.pdf`
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div id="bz-telecom-content" className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl max-w-7xl w-full my-8 border border-cyan-500/30 shadow-2xl shadow-cyan-500/20">
        {/* Header */}
        <div className="relative p-8 border-b border-cyan-500/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                  BZ Telecom - Partenariat Stratégique
                </h2>
                <p className="text-gray-400 mt-1">
                  Gouvernance, KPIs & Roadmap 2026 - Systèmes Téléphonie Cloud Asterisk
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bz-telecom-content-scroll p-8 space-y-8 max-h-[calc(100vh-250px)] overflow-y-auto">
          
          {/* Vue d'ensemble du partenariat */}
          <section className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/20">
            <h3 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
              <Target className="w-6 h-6" />
              Vue d'Ensemble du Partenariat
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white">Contexte</h4>
                <p className="text-gray-300 leading-relaxed">
                  <strong className="text-cyan-400">BZ Telecom</strong> (compagnie sœur) vend des systèmes de téléphonie Cloud basés sur 
                  <strong className="text-blue-400"> Asterisk</strong> avec service humain inclus - c'est le <strong>différenciateur clé</strong>.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  L'équipe <strong className="text-cyan-400">Ventes & Marketing de BZ</strong> agit comme département commercial 
                  en sous-traitance pour BZ Telecom.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white">Engagement Financier</h4>
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Contrat actuel (2025-2026):</span>
                    <span className="text-xl font-bold text-green-400">6,500 $/mois</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">À partir de Jan 2026:</span>
                    <span className="text-xl font-bold text-cyan-400">9,500 $/mois</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-cyan-500/20">
                    <span className="text-gray-400">Augmentation:</span>
                    <span className="text-xl font-bold text-blue-400">+3,000 $/mois</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-cyan-400">10%</div>
                <div className="text-sm text-gray-400 mt-1">Effort Ventes/Marketing dédié à BZ Telecom</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-400">90%</div>
                <div className="text-sm text-gray-400 mt-1">Effort Ventes/Marketing dédié à BZ</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-400">Pro-rata</div>
                <div className="text-sm text-gray-400 mt-1">Répartition entre ressources de l'équipe</div>
              </div>
            </div>
          </section>

          {/* Objectifs 2026 */}
          <section className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/20">
            <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Objectif de Croissance 2026
            </h3>

            {/* WARNING CRITIQUE MRR */}
            <div className="mb-6 bg-gradient-to-r from-red-600/30 via-orange-600/30 to-red-600/30 rounded-xl p-5 border-2 border-red-500/50 shadow-lg shadow-red-500/20">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center border-2 border-red-500">
                    <span className="text-3xl">⚠️</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-xl font-bold text-red-400">OBJECTIF CRITIQUE - SURVIE DU CONTRAT</h4>
                    <div className="flex gap-1">
                      <span className="text-yellow-400 text-lg">⭐</span>
                      <span className="text-yellow-400 text-lg">⭐</span>
                      <span className="text-yellow-400 text-lg">⭐</span>
                      <span className="text-yellow-400 text-lg">⭐</span>
                      <span className="text-yellow-400 text-lg">⭐</span>
                    </div>
                  </div>
                  <p className="text-gray-200 font-semibold mb-2">
                    L'objectif de <span className="text-white bg-red-500/30 px-2 py-1 rounded">+8,000$/mois MRR</span> n'est PAS optionnel.
                  </p>
                  <p className="text-gray-300 text-sm">
                    <strong className="text-red-400">Si non atteint:</strong> Le contrat entre BZ et BZ Telecom risque d'être <strong className="text-red-400">DÉCHIRÉ</strong>. 
                    Cet objectif est la colonne vertébrale de l'entente et justifie le passage à 9,500$/mois en janvier 2026.
                    <strong className="text-yellow-400"> Toute l'équipe doit comprendre l'enjeu.</strong>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Augmentation MRR visée</div>
                  <div className="text-4xl font-bold text-green-400">+8,000 $/mois</div>
                  <div className="text-sm text-gray-400 mt-2">Revenu Récurrent Mensuel d'ici déc. 2026</div>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Cumul progressif mensuel</div>
                  <div className="text-3xl font-bold text-cyan-400">+666 $/mois</div>
                  <div className="text-sm text-gray-400 mt-2">Moyenne cumulative (8k ÷ 12 mois)</div>
                  <div className="text-xs text-yellow-400 mt-2 bg-yellow-500/10 rounded px-2 py-1">
                    Ex: Jan +400$, Fév +900$ (cumul 1,300$), Mars +625$ (cumul 1,925$), etc.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Panier moyen par client</div>
                  <div className="text-4xl font-bold text-blue-400">275 $/mois</div>
                  <div className="text-sm text-gray-400 mt-2">PME 5+ employés, B2B SMB</div>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Clients nécessaires (2026)</div>
                  <div className="text-3xl font-bold text-purple-400">29 clients</div>
                  <div className="text-sm text-gray-400 mt-2">~2-3 nouveaux clients/mois</div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-4 border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-white">Contrats 60 mois (5 ans)</div>
                  <div className="text-sm text-gray-300">Lifetime Value par client: <span className="text-green-400 font-bold">16,500$</span> (275$ × 60 mois)</div>
                </div>
              </div>
            </div>
          </section>

          {/* RACI Matrix */}
          <section className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/20">
            <h3 className="text-2xl font-bold text-purple-400 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Matrice RACI - Gouvernance Claire
            </h3>
            
            <div className="mb-6 grid md:grid-cols-4 gap-3 text-sm">
              <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-500/30">
                <div className="font-bold text-blue-400">R - Responsible</div>
                <div className="text-gray-300 text-xs mt-1">Fait le travail</div>
              </div>
              <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
                <div className="font-bold text-green-400">A - Accountable</div>
                <div className="text-gray-300 text-xs mt-1">Imputabilité finale</div>
              </div>
              <div className="bg-yellow-500/20 rounded-lg p-3 border border-yellow-500/30">
                <div className="font-bold text-yellow-400">C - Consulted</div>
                <div className="text-gray-300 text-xs mt-1">Consulté avant</div>
              </div>
              <div className="bg-gray-500/20 rounded-lg p-3 border border-gray-500/30">
                <div className="font-bold text-gray-400">I - Informed</div>
                <div className="text-gray-300 text-xs mt-1">Informé après</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-500/30">
                    <th className="text-left p-3 text-purple-400 font-semibold">Activité / Décision</th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Martin<br/><span className="text-xs text-gray-400">(Président BZ Telecom)</span></th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Dany<br/><span className="text-xs text-gray-400">(CA/CE BZ)</span></th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Antoine<br/><span className="text-xs text-gray-400">(Dir. Ventes BZ)</span></th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Jérôme<br/><span className="text-xs text-gray-400">(Dir. Marketing BZ)</span></th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Équipe Ventes<br/><span className="text-xs text-gray-400">(Antoine)</span></th>
                    <th className="text-center p-3 text-purple-400 font-semibold">Dany<br/><span className="text-xs text-gray-400">(Président BZ)</span></th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  {/* Stratégie d'affaires */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Stratégie d'affaires BZ Telecom</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                  </tr>
                  
                  {/* Plan tactique Marketing/Ventes */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Plan tactique Marketing/Ventes</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                  </tr>

                  {/* Plan opérationnel Ventes */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Plan opérationnel Ventes</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Exécution technique Ventes */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Exécution technique (prospection, démos, closing)</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Formation produit */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Formation produit (montrer comment former)</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Se former (l'équipe) */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Se former (équipe ventes)</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Approbation propositions clients */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Approbation propositions clients</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Pricing & Rabais */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Pricing & Rabais exceptionnels</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Campagnes Marketing */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Campagnes Marketing</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                  </tr>

                  {/* Suivi KPIs */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5">
                    <td className="p-3 font-medium">Suivi KPIs & Performance</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-yellow-500/30 text-yellow-400 rounded font-bold">C</span></td>
                  </tr>

                  {/* Outil de vente en ligne */}
                  <tr className="border-b border-purple-500/10 hover:bg-purple-500/5 bg-green-500/5">
                    <td className="p-3 font-medium">🛒 Outil de vente en ligne (Configurateur pricing auto)</td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-blue-500/30 text-blue-400 rounded font-bold">R</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-gray-500/30 text-gray-400 rounded font-bold">I</span></td>
                    <td className="text-center p-3"><span className="px-3 py-1 bg-green-500/30 text-green-400 rounded font-bold">A</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
              <p className="text-gray-300 text-sm">
                <strong className="text-purple-400">Note importante:</strong> Martin a l'imputabilité finale (Accountable) 
                sur la stratégie et le plan tactique. Antoine et son équipe ont l'imputabilité sur l'opérationnel et le technique. 
                Dany est Consulté sur les décisions stratégiques et financières importantes.
              </p>
            </div>
          </section>

          {/* Guide de vente - Du lead au client live */}
          <section className="bg-gradient-to-br from-sky-500/10 via-slate-900/40 to-cyan-500/10 rounded-xl p-6 border border-sky-500/30">
            <h3 className="text-2xl font-bold text-sky-400 mb-4 flex items-center gap-2">
              <Lightbulb className="w-6 h-6" />
              Guide de vente BZ Telecom – du lead au client live
            </h3>

            <div className="grid md:grid-cols-3 gap-6 text-sm">
              {/* Workflow simple */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-sky-300" />
                  Workflow du parfait vendeur
                </h4>
                <ol className="space-y-2 text-gray-300 list-decimal list-inside">
                  <li>
                    <span className="font-semibold text-sky-200">Lead qualifié (fit rapide)</span>
                    <div className="text-xs text-gray-400">
                      Comprendre en 2–3 phrases qui est le client, son nombre d&apos;employés, ses sites, et s&apos;il a
                      déjà une téléphonie IP ou un vieux PBX.
                    </div>
                  </li>
                  <li>
                    <span className="font-semibold text-sky-200">Prise de besoin structurée</span>
                    <div className="text-xs text-gray-400">
                      Utiliser les questions ci-contre pour cartographier le système actuel, les irritants et les
                      objectifs (fiabilité, télétravail, multi-sites, IA, etc.).
                    </div>
                  </li>
                  <li>
                    <span className="font-semibold text-sky-200">Scénarios & soumission</span>
                    <div className="text-xs text-gray-400">
                      Générer 1–2 scénarios simples à partir du configurateur BZ Telecom (ex. &quot;Essentiel&quot; vs
                      &quot;Complet&quot;), avec un message clair sur la flexibilité (sans engagement long terme).
                    </div>
                  </li>
                  <li>
                    <span className="font-semibold text-sky-200">Validation & réponses aux objections</span>
                    <div className="text-xs text-gray-400">
                      Passer à travers les points sensibles : fournisseur actuel, fiabilité, télétravail, multi-sites.
                      Utiliser les scripts d&apos;objections pour rassurer.
                    </div>
                  </li>
                  <li>
                    <span className="font-semibold text-sky-200">Closing & passage à l&apos;implantation</span>
                    <div className="text-xs text-gray-400">
                      Confirmer l&apos;option choisie, les dates cibles, les sites impliqués. Transférer un dossier
                      complet (questionnaire, schéma, contraintes) à l&apos;équipe technique.
                    </div>
                  </li>
                </ol>
              </div>

              {/* Questions de prise de besoin */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-emerald-300" />
                  Questions clés de prise de besoin
                </h4>
                <div className="space-y-3 text-gray-300">
                  <div>
                    <p className="font-semibold text-emerald-200 text-xs uppercase mb-1">1. Contexte & entreprise</p>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Combien d&apos;employés et de postes téléphoniques actifs aujourd&apos;hui?</li>
                      <li>Combien de sites physiques? Combien de télétravailleurs réguliers?</li>
                      <li>Quel est votre secteur d&apos;activité principal?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-xs uppercase mb-1">
                      2. Téléphonie actuelle & fournisseur
                    </p>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Quel fournisseur et quel type de système utilisez-vous (PBX, VoIP, cloud)?</li>
                      <li>Êtes-vous lié par un contrat? Jusqu&apos;à quand?</li>
                      <li>Quelles sont les 2–3 choses qui vous agacent le plus avec votre système actuel?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-xs uppercase mb-1">
                      3. Flux d&apos;appels & fonctionnalités
                    </p>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Comment se déroule un appel type (accueil, file, transfert, messagerie)?</li>
                      <li>Avez-vous besoin d&apos;enregistrements, de files d&apos;attente avancées ou de rapports?</li>
                      <li>La téléphonie doit-elle s&apos;intégrer à d&apos;autres systèmes (CRM, Teams, etc.)?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-xs uppercase mb-1">
                      4. Infrastructure & connectivité
                    </p>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Quel type d&apos;accès Internet avez-vous (fibre, câble, autre)?</li>
                      <li>Le câblage structuré est-il déjà en place? Y a-t-il des zones problématiques?</li>
                      <li>Les utilisateurs doivent-ils pouvoir travailler de n&apos;importe où (maison, déplacement)?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-xs uppercase mb-1">
                      5. Décision & budget
                    </p>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Qui doit être autour de la table pour décider?</li>
                      <li>Y a-t-il une fenêtre idéale pour le changement (fin de contrat, ouverture de site)?</li>
                      <li>Avez-vous une fourchette de budget mensuel en tête pour la téléphonie?</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Objections & scripts */}
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-300" />
                  Objections courantes & réponses
                </h4>
                <div className="space-y-3 text-xs text-gray-300">
                  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">« On a déjà un fournisseur. »</p>
                    <p className="mt-1">
                      <span className="text-gray-400">Réponse :</span> C&apos;est parfait, ça veut dire que la
                      téléphonie est importante pour vous. Ce qu&apos;on propose, ce n&apos;est pas juste un autre
                      système, c&apos;est un service local, flexible, sans engagement long terme, avec un vrai humain
                      qui prend en charge vos ajustements au quotidien. On peut comparer sur une base simple : qualité,
                      flexibilité et service.
                    </p>
                  </div>
                  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">« On ne veut pas être pris dans un contrat. »</p>
                    <p className="mt-1">
                      <span className="text-gray-400">Réponse :</span> Justement, BZ Telecom se distingue par l&apos;absence
                      d&apos;engagement long terme. On mise sur la qualité du service et la relation, pas sur des clauses
                      qui vous enferment. Si on ne livre pas, vous n&apos;êtes pas coincés.
                    </p>
                  </div>
                  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">« Est-ce que c&apos;est fiable? On ne peut pas se permettre des coupures. »</p>
                    <p className="mt-1">
                      <span className="text-gray-400">Réponse :</span> La plateforme est pensée pour la disponibilité :
                      redondance de site, monitoring, et une équipe locale qui connaît votre réalité. On valide ensemble
                      vos besoins en redondance (Internet, sites) pour que la solution tienne la route avant de signer.
                    </p>
                  </div>
                  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">
                      « Avec le télétravail et nos sites multiples, ça va être compliqué. »
                    </p>
                    <p className="mt-1">
                      <span className="text-gray-400">Réponse :</span> La force d&apos;une téléphonie IP hébergée, c&apos;est
                      justement de simplifier le multi-sites et le télétravail. On pense en &quot;extension logique&quot;
                      : vos équipes sont sur le même système, peu importe d&apos;où elles travaillent. On prend le temps
                      de cartographier vos sites et vos profils utilisateurs pour que ce soit simple à vivre pour eux.
                    </p>
                  </div>
                  <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">Bonus IA (à utiliser en fin de discussion)</p>
                    <p className="mt-1">
                      L&apos;IA est un bonus : routage plus intelligent, rapports plus clairs, automatisation de
                      certaines tâches. On commence par vous donner une base solide et fiable, puis on ajoute l&apos;IA
                      là où ça fait vraiment une différence pour vos équipes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* KPIs */}
          <section className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/20">
            <h3 className="text-2xl font-bold text-orange-400 mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              KPIs - Actions Contrôlables & Résultats
            </h3>
            
            <div className="mb-4 bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
              <p className="text-gray-300 text-sm">
                <strong className="text-orange-400">Principe fondamental:</strong> On contrôle les ACTIONS, pas les RÉSULTATS. 
                Les KPIs d'actions sont donc prioritaires pour le suivi hebdomadaire/mensuel.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* KPIs d'Actions */}
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-orange-400 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  KPIs d'Actions (Contrôlables)
                </h4>
                
                <div className="space-y-3">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Propositions envoyées</div>
                      <div className="text-orange-400 font-bold">3-4/mois</div>
                    </div>
                    <div className="text-sm text-gray-400">Par vendeur - Soumissions complètes</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Follow-ups dans 24h</div>
                      <div className="text-orange-400 font-bold">95%+</div>
                    </div>
                    <div className="text-sm text-gray-400">Taux de suivi après démo/proposition</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">CRM à jour</div>
                      <div className="text-orange-400 font-bold">100%</div>
                    </div>
                    <div className="text-sm text-gray-400">Notes & statuts mis à jour quotidiennement</div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Traçabilité des leads</div>
                      <div className="text-orange-400 font-bold">80%</div>
                    </div>
                    <div className="text-sm text-gray-400">Leads entrants avec provenance documentée (source marketing/vendeurs)</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Formation produit</div>
                      <div className="text-orange-400 font-bold">3 bootcamps/année</div>
                    </div>
                    <div className="text-sm text-gray-400">Bootcamps structurés dirigés par Martin pour maîtriser Asterisk/BZ Telecom</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Contenu Réseaux Sociaux</div>
                      <div className="text-orange-400 font-bold">1/mois</div>
                    </div>
                    <div className="text-sm text-gray-400">Équipe - Visibilité & thought leadership</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border-l-2 border-purple-500">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">🎬 Vidéo Sora2 (dès Jan 2026)</div>
                      <div className="text-orange-400 font-bold">3/an</div>
                    </div>
                    <div className="text-sm text-gray-300 font-medium mb-1">Concept: "BZ Telecom en 60 secondes"</div>
                    <div className="text-xs text-gray-400">Capsules IA trimestrielles: Tips télécom, success stories clients, features spotlight - Format court & engageant</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border-l-2 border-green-500">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">🛒 Outil Vente en Ligne Clé en Main</div>
                      <div className="text-orange-400 font-bold">Lancement Q2</div>
                    </div>
                    <div className="text-sm text-gray-300 font-medium mb-1">Configurateur web avec pricing automatique</div>
                    <div className="text-xs text-gray-400">Prospect configure son système → Prix instantané → Proposition générée automatiquement → Réduction cycle vente</div>
                  </div>
                </div>
              </div>

              {/* KPIs de Résultats */}
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-blue-400 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  KPIs de Résultats (Outcome)
                </h4>
                
                <div className="space-y-3">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">MRR ajouté (cumul progressif)</div>
                      <div className="text-blue-400 font-bold">+666$/mois</div>
                    </div>
                    <div className="text-sm text-gray-400">Moyenne cumulative - Total: +8k$/mois d'ici déc 2026</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Nouveaux clients</div>
                      <div className="text-blue-400 font-bold">2-3/mois</div>
                    </div>
                    <div className="text-sm text-gray-400">Basé sur panier moyen 275$/mois</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border-l-2 border-yellow-500">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Taux closing Proposition → Contrat</div>
                      <div className="text-blue-400 font-bold">30-40%</div>
                    </div>
                    <div className="text-sm text-gray-400 mb-2">Objectif basé sur industrie standard B2B SMB</div>
                    <div className="text-xs text-yellow-400 bg-yellow-500/10 rounded px-2 py-1 border border-yellow-500/20">
                      ⚠️ Note Antoine: Objectif ambitieux - Aucune donnée historique BZ Telecom disponible. À ajuster après Q1-Q2 2026.
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Valeur moyenne contrat</div>
                      <div className="text-blue-400 font-bold">275$/mois</div>
                    </div>
                    <div className="text-sm text-gray-400">Panier moyen (à optimiser vers 350$+)</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Cycle de vente</div>
                      <div className="text-blue-400 font-bold">30-45 jours</div>
                    </div>
                    <div className="text-sm text-gray-400">Lead → Closing (cible à réduire)</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Traçabilité leads</div>
                      <div className="text-blue-400 font-bold">80%</div>
                    </div>
                    <div className="text-sm text-gray-400">Au moins 80% des opportunités ont une source connue et documentée</div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4 border-l-2 border-red-500">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Funnel ventes</div>
                      <div className="text-blue-400 font-bold">18 → 100</div>
                    </div>
                    <div className="text-sm text-gray-400">Augmenter le pipeline à 100 opportunités qualifiées d'ici 31 mars 2026 pour sécuriser le cashflow</div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border-l-2 border-pink-500">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-white">Validation ROI Martin</div>
                      <div className="text-blue-400 font-bold">Fin Q1 2026</div>
                    </div>
                    <div className="text-sm text-gray-400">Décision officielle: poursuivre ou interrompre le contrat selon le retour sur investissement réel des ventes</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-blue-500/10 rounded-lg p-5 border border-blue-500/30">
              <h4 className="text-lg font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                KPIs Critiques par Trimestre (Survie du Contrat)
              </h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
                <div className="bg-slate-900/40 rounded-lg p-4 border border-cyan-500/30">
                  <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Q1 2026</div>
                  <div className="text-3xl font-bold text-white mt-2">+1,925$/mois</div>
                  <div className="text-xs text-cyan-300 mt-1">MRR cumulatif minimal</div>
                  <p className="text-xs text-gray-400 mt-2">Si non atteint: alerte rouge & revue immédiate avant avril.</p>
                </div>
                <div className="bg-slate-900/40 rounded-lg p-4 border border-green-500/30">
                  <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Q2 2026</div>
                  <div className="text-3xl font-bold text-white mt-2">+3,850$/mois</div>
                  <div className="text-xs text-green-300 mt-1">MRR cumulatif minimal</div>
                  <p className="text-xs text-gray-400 mt-2">Checkpoint décisionnel Martin: maintien ou ajustement majeur.</p>
                </div>
                <div className="bg-slate-900/40 rounded-lg p-4 border border-orange-500/30">
                  <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Q3 2026</div>
                  <div className="text-3xl font-bold text-white mt-2">+5,775$/mois</div>
                  <div className="text-xs text-orange-300 mt-1">MRR cumulatif minimal</div>
                  <p className="text-xs text-gray-400 mt-2">Si ce seuil n'est pas rencontré, Martin devra planifier l'arrêt du contrat avec BZ.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Roadmap 2026 */}
          <section className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/20">
            <h3 className="text-2xl font-bold text-indigo-400 mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Roadmap 2026 - Plan d'Action par Trimestre
            </h3>

            <div className="space-y-6">
              {/* Q1 */}
              <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-cyan-500">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-cyan-400">Q1 2026 (Jan - Mar)</h4>
                    <p className="text-gray-400 text-sm">Fondations & Formation</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Objectif MRR</div>
                    <div className="text-2xl font-bold text-cyan-400">+1,925$</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-white mb-2">🎯 Jalons clés</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Formation intensive produit Asterisk (équipe)</li>
                      <li>• Définition ICP & personas clients</li>
                      <li>• Mise en place CRM & processus ventes</li>
                      <li>• Création scripts de prospection</li>
                      <li>• 1ère campagne LinkedIn (Jerome)</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">📊 KPIs Actions</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• 3 propositions/mois/vendeur (ramp-up → 3-4)</li>
                      <li>• 95%+ follow-ups dans 24h</li>
                      <li>• 100% CRM à jour</li>
                      <li>• Bootcamp produit #1 complété (Martin → équipe)</li>
                      <li>• 80% des leads avec provenance documentée</li>
                      <li>• 🎬 Capsule Sora2 #1 (sur 3) publiée</li>
                      <li>• Pipeline porté à 100 opportunités qualifiées (avant 31 mars)</li>
                      <li>• 7 nouveaux clients cibles (1,925$ MRR)</li>
                      <li>• ✅ Validation ROI Martin (fin mars) pour décider du contrat</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Q2 */}
              <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-green-500">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-green-400">Q2 2026 (Avr - Juin)</h4>
                    <p className="text-gray-400 text-sm">Accélération & Optimisation</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Objectif MRR</div>
                    <div className="text-2xl font-bold text-green-400">+1,925$</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-white mb-2">🎯 Jalons clés</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Optimisation scripts après retours terrain</li>
                      <li>• 🛒 Lancement outil vente en ligne (pricing auto)</li>
                      <li>• Lancement programme de référencement</li>
                      <li>• Création contenu (études de cas, vidéos)</li>
                      <li>• Partenariats stratégiques (2-3 partenaires)</li>
                      <li>• 1er webinaire BZ Telecom</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">📊 KPIs Actions</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• 3-4 propositions/mois/vendeur (pleine vitesse)</li>
                      <li>• 95%+ follow-ups dans 24h</li>
                      <li>• 100% CRM à jour</li>
                      <li>• 80% provenance leads maintenue (audit mensuel)</li>
                      <li>• 🎬 Capsule Sora2 #2 (sur 3) en ligne</li>
                      <li>• Bootcamp produit #2 (démos avancées)</li>
                      <li>• 7 nouveaux clients (1,925$ MRR)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Q3 */}
              <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-orange-500">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-orange-400">Q3 2026 (Juil - Sep)</h4>
                    <p className="text-gray-400 text-sm">Scaling & Innovation</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Objectif MRR</div>
                    <div className="text-2xl font-bold text-orange-400">+1,925$</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-white mb-2">🎯 Jalons clés</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Automatisation prospection LinkedIn (AI)</li>
                      <li>• Lancement offre premium (300-400$/mois)</li>
                      <li>• Campagne email nurturing automatisée</li>
                      <li>• Participation événement/salon (1-2)</li>
                      <li>• Formation avancée équipe (upsell)</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">📊 KPIs Actions</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• 3-4 propositions/mois/vendeur</li>
                      <li>• 95%+ follow-ups dans 24h</li>
                      <li>• 100% CRM à jour</li>
                      <li>• 1 contenu réseaux sociaux/mois</li>
                      <li>• 80% provenance leads maintenue</li>
                      <li>• 🎬 Capsule Sora2 #3 (sur 3) + optimisation IA</li>
                      <li>• 7 nouveaux clients (1,925$ MRR)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Q4 */}
              <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-purple-500">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold text-purple-400">Q4 2026 (Oct - Déc)</h4>
                    <p className="text-gray-400 text-sm">Push Final & Pérennisation</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Objectif MRR</div>
                    <div className="text-2xl font-bold text-purple-400">+2,200$</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-white mb-2">🎯 Jalons clés</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Campagne fin d'année (urgence fiscale)</li>
                      <li>• Révision & optimisation processus 2026</li>
                      <li>• Planification stratégique 2026</li>
                      <li>• Renégociation contrat BZ/BZ Telecom</li>
                      <li>• Bilan année & célébration équipe 🎉</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">📊 KPIs Actions</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• 4-5 propositions/mois/vendeur (push final)</li>
                      <li>• 95%+ follow-ups dans 24h</li>
                      <li>• 100% CRM à jour</li>
                      <li>• 1 contenu réseaux sociaux/mois</li>
                      <li>• 80% provenance leads maintenue (revue trimestrielle)</li>
                      <li>• 🎬 Amplification des 3 capsules Sora2 existantes</li>
                      <li>• 8 nouveaux clients (2,200$ MRR)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-5 border border-green-500/30">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xl font-bold text-green-400 mb-2">Objectif Annuel Total</div>
                  <div className="text-gray-300">
                    <strong className="text-white">+7,975$/mois MRR</strong> atteint avec 
                    <strong className="text-cyan-400"> 29 nouveaux clients</strong> sur l'année 2026. 
                    LTV total: <strong className="text-green-400">478,500$</strong> (29 clients × 16,500$)
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cadre de Pricing Rigide */}
          <section className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/20">
            <h3 className="text-2xl font-bold text-emerald-400 mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Cadre de Pricing Rigide & Standardisé
            </h3>

            <div className="mb-6 bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
              <p className="text-gray-300">
                <strong className="text-emerald-400">Principe fondamental:</strong> Un cadre de pricing clair, rigide et pré-établi 
                est ESSENTIEL pour permettre l'outil de vente en ligne et simplifier le travail de l'équipe ventes. 
                Fini les négociations complexes - tout est transparent et standardisé.
              </p>
            </div>

            <div className="space-y-6">
              {/* Structure de pricing */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <h4 className="text-lg font-semibold text-emerald-400 mb-4">📋 Structure de Pricing Proposée</h4>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Package Essentiel */}
                  <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg p-4 border border-blue-500/30">
                    <div className="text-center mb-3">
                      <div className="text-sm text-blue-400 font-semibold uppercase tracking-wide">Essentiel</div>
                      <div className="text-3xl font-bold text-white mt-2">175$/mois</div>
                      <div className="text-xs text-gray-400 mt-1">1-5 utilisateurs</div>
                    </div>
                    <ul className="text-xs text-gray-300 space-y-1.5">
                      <li>✓ Système VoIP Asterisk Cloud</li>
                      <li>✓ Lignes téléphoniques illimitées</li>
                      <li>✓ Support technique 5/7</li>
                      <li>✓ Boîte vocale intelligente</li>
                      <li>✓ Application mobile</li>
                    </ul>
                  </div>

                  {/* Package Professionnel */}
                  <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-lg p-4 border-2 border-emerald-500/50 relative">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">POPULAIRE</span>
                    </div>
                    <div className="text-center mb-3">
                      <div className="text-sm text-emerald-400 font-semibold uppercase tracking-wide">Professionnel</div>
                      <div className="text-3xl font-bold text-white mt-2">275$/mois</div>
                      <div className="text-xs text-gray-400 mt-1">6-15 utilisateurs</div>
                    </div>
                    <ul className="text-xs text-gray-300 space-y-1.5">
                      <li>✓ Tout Essentiel +</li>
                      <li>✓ Support 24/7 prioritaire</li>
                      <li>✓ Enregistrement des appels</li>
                      <li>✓ Répartition intelligente</li>
                      <li>✓ Statistiques avancées</li>
                      <li>✓ Intégration CRM</li>
                    </ul>
                  </div>

                  {/* Package Entreprise */}
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg p-4 border border-purple-500/30">
                    <div className="text-center mb-3">
                      <div className="text-sm text-purple-400 font-semibold uppercase tracking-wide">Entreprise</div>
                      <div className="text-3xl font-bold text-white mt-2">425$/mois</div>
                      <div className="text-xs text-gray-400 mt-1">16-50 utilisateurs</div>
                    </div>
                    <ul className="text-xs text-gray-300 space-y-1.5">
                      <li>✓ Tout Professionnel +</li>
                      <li>✓ Gestionnaire de compte dédié</li>
                      <li>✓ SLA garanti 99.9%</li>
                      <li>✓ Centre d'appels complet</li>
                      <li>✓ Analytics IA</li>
                      <li>✓ Personnalisation complète</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-400 text-center">
                  * Prix par tranche d'utilisateurs. Entreprise 50+ : pricing sur mesure avec gestionnaire dédié
                </div>
              </div>

              {/* Options Add-ons */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <h4 className="text-lg font-semibold text-emerald-400 mb-4">🔧 Options Add-ons (à la carte)</h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Formation équipe (par session)</span>
                      <span className="text-emerald-400 font-bold">150$</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Migration de système existant</span>
                      <span className="text-emerald-400 font-bold">500$</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Numéro sans frais additionnel</span>
                      <span className="text-emerald-400 font-bold">+25$/mois</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Sauvegarde appels (stockage 1 an)</span>
                      <span className="text-emerald-400 font-bold">+50$/mois</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Support technique dédié (extra)</span>
                      <span className="text-emerald-400 font-bold">+100$/mois</span>
                    </div>
                    <div className="flex justify-between items-center bg-slate-700/30 rounded p-3">
                      <span className="text-gray-300 text-sm">Intégration personnalisée</span>
                      <span className="text-emerald-400 font-bold">Sur devis</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Avantages du pricing rigide */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-5 border border-green-500/30">
                <h4 className="text-lg font-semibold text-green-400 mb-4">✅ Avantages du Pricing Rigide</h4>
                
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Simplicité ventes:</strong> Pas de négociation, prix clair immédiatement</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Outil en ligne possible:</strong> Configurateur automatique fonctionnel</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Équité clients:</strong> Tous les clients paient le même prix (transparent)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Rapidité closing:</strong> Décision plus rapide du prospect</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Prévision revenue:</strong> MRR prévisible et stable</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Moins de friction:</strong> Pas de "je vais demander un rabais"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Focus valeur:</strong> Discussion sur features, pas sur le prix</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300"><strong className="text-white">Scalabilité:</strong> Facile d'ajouter nouveaux packages</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Règles de pricing */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <h4 className="text-lg font-semibold text-emerald-400 mb-4">📐 Règles de Pricing (Non-négociables)</h4>
                
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="flex items-start gap-3 bg-red-500/10 rounded p-3 border border-red-500/30">
                    <span className="text-red-400 font-bold">1.</span>
                    <span><strong className="text-red-400">Pas de rabais individuels:</strong> Aucun rabais sans approbation Martin (RACI). Protège l'équité et la marge.</span>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-yellow-500/10 rounded p-3 border border-yellow-500/30">
                    <span className="text-yellow-400 font-bold">2.</span>
                    <span><strong className="text-yellow-400">Rabais volume possible:</strong> 10% pour 3+ ans engagement, ou 5% pour 25+ utilisateurs (pré-autorisé).</span>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-blue-500/10 rounded p-3 border border-blue-500/30">
                    <span className="text-blue-400 font-bold">3.</span>
                    <span><strong className="text-blue-400">Prix affichés = prix finaux:</strong> Ce qui est sur le site/outil = ce que le client paie. Pas de surprise.</span>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-green-500/10 rounded p-3 border border-green-500/30">
                    <span className="text-green-400 font-bold">4.</span>
                    <span><strong className="text-green-400">Révision annuelle:</strong> Prix révisés 1x/an (janvier) selon inflation + coûts. Clients existants: grandfathered 1 an.</span>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-purple-500/10 rounded p-3 border border-purple-500/30">
                    <span className="text-purple-400 font-bold">5.</span>
                    <span><strong className="text-purple-400">Add-ons optionnels:</strong> Upsell via add-ons, pas via rabais sur package de base. Augmente panier moyen.</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Spécifications d'exigences & Prise de besoin */}
          <section className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-6 border border-pink-500/20">
            <h3 className="text-2xl font-bold text-pink-400 mb-4 flex items-center gap-2">
              <GraduationCap className="w-6 h-6" />
              Spécifications d'exigences - Prise de Besoin avec Martin
            </h3>

            <div className="space-y-6">
              <div className="bg-pink-500/10 rounded-lg p-4 border border-pink-500/30">
                <p className="text-gray-300">
                  <strong className="text-pink-400">Contexte:</strong> Cette section documente la prise de besoin initiale 
                  avec Martin pour s'assurer que l'équipe d'Antoine comprend PARFAITEMENT les attentes, la vision et les objectifs de BZ Telecom.
                </p>
              </div>

              {/* Questions clés */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-pink-400">📋 Questions Business</h4>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                    <div>
                      <div className="font-semibold text-white mb-1">1. Vision à 3 ans pour BZ Telecom?</div>
                      <div className="text-sm text-gray-400 italic">Où veux-tu être en 2028? Quelle part de marché? Nombre de clients?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">2. Différenciateur #1 vs compétiteurs?</div>
                      <div className="text-sm text-gray-400 italic">Service humain inclus - mais concrètement, qu'est-ce qui nous rend uniques?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">3. ICP (Ideal Customer Profile) détaillé?</div>
                      <div className="text-sm text-gray-400 italic">Secteurs prioritaires? Taille (5-10, 10-25, 25-50 employés)? Géo?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">4. Stratégie de pricing?</div>
                      <div className="text-sm text-gray-400 italic">Gamme de prix? Upsells? Cross-sells? Modèle freemium?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">5. Partenariats souhaités?</div>
                      <div className="text-sm text-gray-400 italic">Revendeurs? Intégrateurs? Alliances stratégiques?</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-pink-400">🎯 Questions Opérationnelles</h4>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                    <div>
                      <div className="font-semibold text-white mb-1">6. Processus d'onboarding client?</div>
                      <div className="text-sm text-gray-400 italic">Étapes? Délais? Responsables côté BZ Telecom?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">7. Support & SLA garantis?</div>
                      <div className="text-sm text-gray-400 italic">Niveau de service promis? 24/7? Temps de réponse?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">8. Objections clients fréquentes?</div>
                      <div className="text-sm text-gray-400 italic">Prix trop élevé? Migration compliquée? Fiabilité?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">9. Success stories à mettre de l'avant?</div>
                      <div className="text-sm text-gray-400 italic">Clients heureux? ROI démontrables? Témoignages?</div>
                    </div>

                    <div>
                      <div className="font-semibold text-white mb-1">10. Outils/ressources fournis à l'équipe ventes?</div>
                      <div className="text-sm text-gray-400 italic">Démos? Calculateur ROI? Brochures? Vidéos?</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Livrables attendus */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <h4 className="text-lg font-semibold text-pink-400 mb-4">📦 Livrables Attendus de l'Équipe Antoine</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-pink-500/10 rounded-lg p-4 border border-pink-500/20">
                    <div className="font-semibold text-white mb-2">Hebdomadaire</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Rapport activités ventes</li>
                      <li>• Pipeline mis à jour</li>
                      <li>• Top 3 deals en cours</li>
                      <li>• Obstacles rencontrés</li>
                    </ul>
                  </div>

                  <div className="bg-pink-500/10 rounded-lg p-4 border border-pink-500/20">
                    <div className="font-semibold text-white mb-2">Mensuel</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Bilan KPIs actions & résultats</li>
                      <li>• MRR ajouté vs objectif</li>
                      <li>• Retours terrain (insights)</li>
                      <li>• Besoins formation identifiés</li>
                    </ul>
                  </div>

                  <div className="bg-pink-500/10 rounded-lg p-4 border border-pink-500/20">
                    <div className="font-semibold text-white mb-2">Trimestriel</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Révision stratégie Q</li>
                      <li>• Analyse Win/Loss</li>
                      <li>• Propositions d'amélioration</li>
                      <li>• Plan d'action Q suivant</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Idées Stratégiques & Innovation */}
          <section className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 rounded-xl p-6 border border-yellow-500/20">
            <h3 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
              <Lightbulb className="w-6 h-6" />
              Stratégies d'Affaires & Idées de Ventes Innovantes
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Stratégies Marketing */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-yellow-400">🚀 Stratégies Marketing</h4>
                
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-yellow-400">1.</span> Thought Leadership LinkedIn
                    </div>
                    <div className="text-sm text-gray-300">
                      Martin + équipe partagent expertise VoIP, études de cas, tendances télécom. 
                      Objectif: devenir LA référence téléphonie cloud au Québec.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-yellow-400">2.</span> Programme de Référencement
                    </div>
                    <div className="text-sm text-gray-300">
                      Incentives pour clients actuels qui réfèrent (ex: 1 mois gratuit par référence closée). 
                      Les meilleurs ambassadeurs = clients satisfaits.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-yellow-400">3.</span> Webinaires Mensuels Éducatifs
                    </div>
                    <div className="text-sm text-gray-300">
                      "Comment réduire vos coûts télécom de 40%", "Migration vers le Cloud sans interruption", etc. 
                      Lead gen + établir expertise.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-yellow-400">4.</span> Partenariats Verticaux
                    </div>
                    <div className="text-sm text-gray-300">
                      Dentistes, avocats, comptables - secteurs avec besoins télécom spécifiques. 
                      Créer offres packagées par vertical.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-yellow-400">5.</span> Contenu Vidéo YouTube
                    </div>
                    <div className="text-sm text-gray-300">
                      Démos produit, témoignages clients, comparatifs vs compétiteurs. 
                      SEO long terme + ressource pour vendeurs.
                    </div>
                  </div>
                </div>
              </div>

              {/* Tactiques Ventes */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-yellow-400">💼 Tactiques Ventes</h4>
                
                <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">1.</span> Audit Télécom Gratuit
                    </div>
                    <div className="text-sm text-gray-300">
                      Offrir analyse gratuite de leur setup actuel + recommandations. 
                      Hook puissant pour entrer chez prospect.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">2.</span> Période d'Essai 30 Jours
                    </div>
                    <div className="text-sm text-gray-300">
                      Laisser prospects tester système (limité) gratuitement. 
                      Réduire objection du risque. Show don't tell.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">3.</span> 🛒 Outil Vente en Ligne Clé en Main (Q2)
                    </div>
                    <div className="text-sm text-gray-300">
                      Configurateur web interactif: prospect sélectionne nombre d'employés, features désirés → 
                      Pricing instantané + proposition PDF générée automatiquement. Self-service 24/7 + réduction cycle vente.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">4.</span> ROI Calculator Interactif
                    </div>
                    <div className="text-sm text-gray-300">
                      Outil en ligne pour calculer économies vs système actuel. 
                      Données concrètes = closing plus facile.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">5.</span> Bundles Sectoriels
                    </div>
                    <div className="text-sm text-gray-300">
                      Offres pré-packagées par industrie (dentiste, resto, bureau, etc). 
                      Simplifie décision + augmente panier moyen.
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-white flex items-center gap-2 mb-1">
                      <span className="text-cyan-400">6.</span> Social Proof Agressif
                    </div>
                    <div className="text-sm text-gray-300">
                      Études de cas détaillées, témoignages vidéo, badges/certifications. 
                      Surmonter objection "Je ne vous connais pas".
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Innovation IA */}
            <div className="mt-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg p-5 border border-purple-500/30">
              <h4 className="text-lg font-semibold text-purple-400 mb-3 flex items-center gap-2">
                🤖 Innovation IA pour BZ Telecom
              </h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="font-semibold text-white mb-2">Prospection AI</div>
                  <div className="text-gray-300">
                    Scraping LinkedIn automatisé pour identifier décideurs SMB. 
                    Messages personnalisés AI-powered.
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="font-semibold text-white mb-2">Chatbot Qualification</div>
                  <div className="text-gray-300">
                    Bot sur site web qualifie leads 24/7 avant de passer à vendeur. 
                    Économie temps énorme.
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="font-semibold text-white mb-2">Analyse Appels AI</div>
                  <div className="text-gray-300">
                    Transcription + analyse sentiment des calls. 
                    Coaching automatisé pour équipe ventes.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Plan de Formation */}
          <section className="bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-xl p-6 border border-blue-500/20">
            <h3 className="text-2xl font-bold text-blue-400 mb-4 flex items-center gap-2">
              <GraduationCap className="w-6 h-6" />
              Plan de Formation Structuré
            </h3>

            <div className="mb-6 bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
              <p className="text-gray-300">
                <strong className="text-blue-400">Principe:</strong> Martin a la responsabilité de MONTRER comment former. 
                L'équipe d'Antoine a la responsabilité de SE former. Formation continue = succès durable.
              </p>
            </div>

            <div className="space-y-6">
              {/* Module 1 */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-blue-500 text-white font-bold rounded-lg px-4 py-2">Module 1</div>
                  <div>
                    <h4 className="text-xl font-semibold text-white">Fondamentaux Asterisk & VoIP</h4>
                    <p className="text-gray-400 text-sm">Durée: 4 heures | Format: Présentiel avec Martin</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-blue-400 mb-2">Contenu</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Qu'est-ce qu'Asterisk? Histoire & avantages</li>
                      <li>• Architecture VoIP vs téléphonie traditionnelle</li>
                      <li>• Fonctionnalités principales du système BZ Telecom</li>
                      <li>• Démo live plateforme admin</li>
                      <li>• Cas d'usage clients réels</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-400 mb-2">Évaluation</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Quiz 10 questions (80% requis pour passer)</li>
                      <li>• Démo simulée devant Martin</li>
                      <li>• Certification interne "Asterisk Basics"</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Module 2 */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-green-500 text-white font-bold rounded-lg px-4 py-2">Module 2</div>
                  <div>
                    <h4 className="text-xl font-semibold text-white">Proposition de Valeur & Discovery</h4>
                    <p className="text-gray-400 text-sm">Durée: 3 heures | Format: Workshop Dany + Antoine</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-green-400 mb-2">Contenu</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Le différenciateur: Service humain inclus</li>
                      <li>• ICP BZ Telecom (PME 5+ employés)</li>
                      <li>• Questions SPIN pour discovery télécom</li>
                      <li>• Gestion objections prix/migration/fiabilité</li>
                      <li>• Storytelling & études de cas</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-green-400 mb-2">Évaluation</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Jeu de rôle discovery call (enregistré)</li>
                      <li>• Présentation 5 min value prop</li>
                      <li>• Feedback 360° de l'équipe</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Module 3 */}
              <div className="bg-slate-800/50 rounded-lg p-5">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-purple-500 text-white font-bold rounded-lg px-4 py-2">Module 3</div>
                  <div>
                    <h4 className="text-xl font-semibold text-white">Démo Produit & Closing</h4>
                    <p className="text-gray-400 text-sm">Durée: 3 heures | Format: Présentiel Martin + Antoine</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="font-semibold text-purple-400 mb-2">Contenu</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Structure démo parfaite (20-30 min)</li>
                      <li>• Fonctionnalités à montrer selon ICP</li>
                      <li>• Techniques closing consultatives</li>
                      <li>• Proposition commerciale & pricing</li>
                      <li>• Processus contractuel & onboarding</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-purple-400 mb-2">Évaluation</div>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Démo complète (enregistrée & critiquée)</li>
                      <li>• Création proposition custom</li>
                      <li>• Jeu de rôle closing + objections</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Formation continue */}
              <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg p-5 border border-cyan-500/30">
                <h4 className="text-lg font-semibold text-cyan-400 mb-3">📚 Formation Continue (Ongoing)</h4>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-white mb-2">Hebdomadaire</div>
                    <div className="text-gray-300">
                      • 30 min de veille techno VoIP<br/>
                      • Partage wins/losses équipe<br/>
                      • Tips & tricks Martin
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">Mensuel</div>
                    <div className="text-gray-300">
                      • 1 webinaire externe (Asterisk, VoIP)<br/>
                      • Analyse call enregistré (coaching)<br/>
                      • Nouvelle feature produit
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-2">Trimestriel</div>
                    <div className="text-gray-300">
                      • Formation avancée (upsell, etc)<br/>
                      • Visite client avec Martin<br/>
                      • Certification industrie (optionnel)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Gouvernance & Comité */}
          <section className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl p-6 border border-red-500/20">
            <h3 className="text-2xl font-bold text-red-400 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Gouvernance - Comité BZ Telecom
            </h3>

            <div className="space-y-6">
              <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                <p className="text-gray-300">
                  <strong className="text-red-400">Fréquence:</strong> Rencontre mensuelle pour révision performance, 
                  ajustements tactiques et alignment stratégique entre BZ et BZ Telecom.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-5">
                  <h4 className="text-lg font-semibold text-red-400 mb-3">🗓️ Structure Rencontre Mensuelle</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">10min</div>
                      <div className="text-gray-300">Check-in & tour de table</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">15min</div>
                      <div className="text-gray-300">Revue KPIs mois précédent (actions + résultats)</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">20min</div>
                      <div className="text-gray-300">Pipeline review - Top deals en cours</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">10min</div>
                      <div className="text-gray-300">Obstacles & challenges (résolution collaborative)</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">10min</div>
                      <div className="text-gray-300">Insights terrain (retours équipe ventes)</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">10min</div>
                      <div className="text-gray-300">Ajustements tactiques & décisions</div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-gray-400 font-mono">5min</div>
                      <div className="text-gray-300">Actions & responsables (RACI clair)</div>
                    </div>
                    <div className="border-t border-red-500/20 pt-2 mt-2">
                      <div className="flex gap-3">
                        <div className="text-red-400 font-mono font-bold">80min</div>
                        <div className="text-white font-semibold">Total</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-5">
                  <h4 className="text-lg font-semibold text-red-400 mb-3">👥 Participants</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5"></div>
                      <div>
                        <div className="font-semibold text-white">Martin (Président BZ Telecom)</div>
                        <div className="text-sm text-gray-400">Accountable - Décisions finales</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mt-1.5"></div>
                      <div>
                        <div className="font-semibold text-white">Dany (CA/CE BZ)</div>
                        <div className="text-sm text-gray-400">Responsible plans tactiques</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mt-1.5"></div>
                      <div>
                        <div className="font-semibold text-white">Antoine (Dir. Ventes BZ)</div>
                        <div className="text-sm text-gray-400">Responsible exécution terrain</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5"></div>
                      <div>
                        <div className="font-semibold text-white">Jérôme (Dir. Marketing BZ)</div>
                        <div className="text-sm text-gray-400">Responsible campagnes marketing</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mt-1.5"></div>
                      <div>
                        <div className="font-semibold text-white">Dany (Président BZ)</div>
                        <div className="text-sm text-gray-400">Consulté sur décisions majeures</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-5 border border-green-500/30">
                <h4 className="text-lg font-semibold text-green-400 mb-3">✅ Critères de Succès du Comité</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <ul className="text-gray-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Décisions claires avec RACI assignés</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Alignment total entre Martin, Dany et Antoine</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Actions concrètes avec deadlines (pas juste discussion)</span>
                    </li>
                  </ul>
                  <ul className="text-gray-300 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Transparence totale (bons & mauvais résultats)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Atmosphère collaborative (pas de blâme)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Focus sur solutions (pas problèmes)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-cyan-500/30 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Dernière mise à jour: <span className="text-cyan-400">Octobre 2024</span> | 
              Document vivant - À réviser trimestriellement
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDownloadPDF}
                className="group px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white rounded-xl transition-all duration-200 font-bold shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:scale-105 flex items-center gap-2 border border-emerald-400/30"
              >
                <Download className="w-5 h-5 group-hover:animate-pulse" />
                <span>Télécharger PDF</span>
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 font-semibold shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-105"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

